import { z } from 'zod';
import { NeedEffects } from './play';
import {
  BabiesContent,
  BabyName,
  ChildView,
  DEFAULT_BABIES,
  DEFAULT_KIN,
  KinLabel,
  KIN_KINDS,
  RelativeView,
  childrenProblems,
} from './children';

// Family, as Lagos Life's 💞 Family (its rules read from the public client code on 21 October 2026), in London
// reality. Partners are other players (the People tie "partner"): up to four while dating, one spouse at a time
// (marrying ends the other partnerships; Jerry, 22 October). A couple goes on dates at places on the map, gets
// engaged with a ring, and marries in a wedding of their choice at a venue on the map. What they do together
// counts towards couple goals and a couple level. Children and relatives are in children.ts. All of it is
// content the admins edit.

const id = z.string().regex(/^[a-z0-9_]{2,30}$/);
const placeId = z.string().regex(/^[a-z0-9_]{2,40}$/);
const effects = NeedEffects;

export const DateSpot = z.object({
  id,
  placeId,
  label: z.string().trim().min(2).max(50),
  emoji: z.string().trim().min(1).max(16),
  // paid by the one who asks (held until the other answers)
  pricePence: z.number().int().min(0).max(10_000_000),
  // what the date does for both of them, and how much closer they grow
  effects,
  closeness: z.number().int().min(0).max(30),
  visible: z.boolean(),
});
export type DateSpot = z.infer<typeof DateSpot>;
export const WeddingTier = z.object({
  id,
  label: z.string().trim().min(2).max(40),
  emoji: z.string().trim().min(1).max(16),
  line: z.string().trim().min(5).max(120),
  pricePence: z.number().int().min(0).max(1_000_000_000),
  guests: z.number().int().min(2).max(1_000),
  visible: z.boolean(),
});
export type WeddingTier = z.infer<typeof WeddingTier>;
export const WeddingVenue = z.object({
  placeId,
  label: z.string().trim().min(2).max(50),
  emoji: z.string().trim().min(1).max(16),
  visible: z.boolean(),
});
export type WeddingVenue = z.infer<typeof WeddingVenue>;
export const COUPLE_GOAL_KEYS = ['dates', 'nights', 'trips', 'anniversaries'] as const;
export type CoupleGoalKey = (typeof COUPLE_GOAL_KEYS)[number];
export const CoupleGoal = z.object({
  key: z.enum(COUPLE_GOAL_KEYS),
  label: z.string().trim().min(2).max(40),
  emoji: z.string().trim().min(1).max(16),
  // each step reached is a point towards the couple level
  steps: z.array(z.number().int().min(1).max(10_000)).min(1).max(10),
});
export type CoupleGoal = z.infer<typeof CoupleGoal>;
export const CoupleLevel = z.object({
  at: z.number().int().min(0).max(100),
  title: z.string().trim().min(2).max(40),
  emoji: z.string().trim().min(1).max(16),
});
export type CoupleLevel = z.infer<typeof CoupleLevel>;

export const FamilyContent = z.object({
  visible: z.boolean(),
  maxPartners: z.number().int().min(1).max(10),
  // how close two partners must be to propose, and how long they stay engaged before the wedding
  proposeCloseness: z.number().int().min(0).max(100),
  engagedHours: z
    .number()
    .int()
    .min(0)
    .max(24 * 30),
  ringPence: z.number().int().min(0).max(100_000_000),
  // an ask (a date, a proposal, a wedding) waits this long for an answer, then the money goes back
  askHours: z
    .number()
    .int()
    .min(1)
    .max(24 * 7),
  // one date per couple in this many hours; one night in counted per day
  dateEveryHours: z
    .number()
    .int()
    .min(0)
    .max(24 * 7),
  // what the wedding does for both of them
  weddingEffects: effects,
  anniversaryDays: z.number().int().min(1).max(365),
  dateSpots: z.array(DateSpot).min(1).max(30),
  weddingTiers: z.array(WeddingTier).min(1).max(10),
  weddingVenues: z.array(WeddingVenue).min(1).max(20),
  goals: z.array(CoupleGoal).length(COUPLE_GOAL_KEYS.length),
  levels: z.array(CoupleLevel).min(1).max(20),
  // children and relatives (children.ts)
  babies: BabiesContent,
  kin: z.array(KinLabel).length(KIN_KINDS.length),
});
export type FamilyContent = z.infer<typeof FamilyContent>;

const spot = (
  id: string,
  placeId: string,
  label: string,
  emoji: string,
  pounds: number,
  fun: number,
  social: number,
): DateSpot => ({
  id,
  placeId,
  label,
  emoji,
  pricePence: pounds * 100,
  effects: { fun, social },
  closeness: 6,
  visible: true,
});
const goal = (key: CoupleGoalKey, label: string, emoji: string, steps: number[]): CoupleGoal => ({
  key,
  label,
  emoji,
  steps,
});
export const DEFAULT_FAMILY: FamilyContent = {
  visible: true,
  maxPartners: 4,
  proposeCloseness: 60,
  engagedHours: 24,
  ringPence: 150_000,
  askHours: 24,
  dateEveryHours: 24,
  weddingEffects: { fun: 40, social: 40 },
  anniversaryDays: 7,
  dateSpots: [
    spot('rooftop', 'roofgarden', 'Dinner at the Roof Garden', '🕯️', 85, 22, 18),
    spot('o2', 'o2', 'Dinner and a film at The O2', '🍿', 45, 20, 14),
    spot('picnic', 'park', 'Picnic in Greenwich Park', '🧺', 18, 16, 16),
    spot('cablecar', 'cablecar', 'Sunset on the cable car', '🚡', 12, 18, 12),
    spot('stars', 'observatory', 'Stargazing at the Observatory', '🔭', 25, 18, 14),
    spot('river', 'pier', 'A river cruise from Greenwich Pier', '⛴️', 40, 20, 16),
    spot('pub', 'pub', 'Drinks at the Trafalgar Tavern', '🍷', 30, 16, 20),
  ],
  weddingTiers: [
    {
      id: 'register',
      label: 'Register office',
      emoji: '💐',
      line: 'close friends, a meal after and a cake',
      pricePence: 200_000,
      guests: 30,
      visible: true,
    },
    {
      id: 'proper',
      label: 'A proper do',
      emoji: '🎉',
      line: 'the dress, the suits, a band and a sit-down dinner for everyone',
      pricePence: 1_500_000,
      guests: 100,
      visible: true,
    },
    {
      id: 'society',
      label: 'Society wedding',
      emoji: '👑',
      line: 'the wedding London will talk about for years',
      pricePence: 7_500_000,
      guests: 200,
      visible: true,
    },
  ],
  weddingVenues: [
    { placeId: 'stanne', label: "St Anne's Limehouse", emoji: '⛪', visible: true },
    { placeId: 'roofgarden', label: 'Crossrail Place Roof Garden', emoji: '🌿', visible: true },
    { placeId: 'observatory', label: 'The Royal Observatory', emoji: '🔭', visible: true },
    { placeId: 'cuttysark', label: 'Under the Cutty Sark', emoji: '⛵', visible: true },
    { placeId: 'pier', label: 'A boat from Greenwich Pier', emoji: '🛥️', visible: true },
  ],
  goals: [
    goal('dates', 'Dates', '💕', [3, 10, 25, 50, 100]),
    goal('trips', 'Trips together', '✈️', [1, 5, 15, 30]),
    goal('nights', 'Cosy nights in', '🌙', [3, 10, 25, 50]),
    goal('anniversaries', 'Anniversaries', '🥂', [1, 4, 12, 26, 52]),
  ],
  levels: [
    { at: 0, title: 'New couple', emoji: '💞' },
    { at: 2, title: 'Lovebirds', emoji: '🕊️' },
    { at: 5, title: 'Inseparable', emoji: '🫶🏾' },
    { at: 8, title: 'Power couple', emoji: '💪🏾' },
    { at: 12, title: "London's favourite couple", emoji: '🌆' },
    { at: 16, title: 'Forever and ever', emoji: '💍' },
  ],
  babies: DEFAULT_BABIES,
  kin: DEFAULT_KIN,
};
export const familyOf = (content: { family?: FamilyContent }) => content.family ?? DEFAULT_FAMILY;
export function familyProblems(family: FamilyContent, placeIds: Set<string>): string[] {
  const problems: string[] = [];
  const unique = (ids: string[], what: string) => {
    if (new Set(ids).size !== ids.length) problems.push(`Family: two ${what} have the same id.`);
  };
  unique(
    family.dateSpots.map((item) => item.id),
    'date spots',
  );
  unique(
    family.weddingTiers.map((item) => item.id),
    'wedding tiers',
  );
  unique(
    family.weddingVenues.map((item) => item.placeId),
    'wedding venues',
  );
  unique(
    family.goals.map((item) => item.key),
    'couple goals',
  );
  for (const item of [...family.dateSpots, ...family.weddingVenues])
    if (!placeIds.has(item.placeId)) problems.push(`Family: "${item.label}" is at a place that is not on the map.`);
  if (family.levels[0]?.at !== 0) problems.push('Family: the first couple level starts at 0 points.');
  problems.push(...childrenProblems(family.babies, family.kin));
  return problems;
}

// the couple's level from the goal steps reached (each step is a point)
export function coupleLevel(family: FamilyContent, counts: Record<CoupleGoalKey, number>) {
  const points = family.goals.reduce(
    (sum, item) => sum + item.steps.filter((step) => counts[item.key] >= step).length,
    0,
  );
  const levels = [...family.levels].sort((a, b) => a.at - b.at);
  const index = levels.reduce((best, level, i) => (points >= level.at ? i : best), 0);
  return {
    level: index + 1,
    title: levels[index]!.title,
    emoji: levels[index]!.emoji,
    points,
    next: levels[index + 1]?.at ?? null,
  };
}
// anniversaries since the couple got together
export const anniversaries = (family: FamilyContent, since: Date, now: Date) =>
  Math.max(0, Math.floor((now.getTime() - since.getTime()) / (family.anniversaryDays * 86_400_000)));

// ---------- over the wire ----------

export const COUPLE_STAGES = ['dating', 'engaged', 'married'] as const;
export type CoupleStage = (typeof COUPLE_STAGES)[number];
// a date, the ring, the wedding; a baby, an adoption (both name the child), or a new name for a child
export const FAMILY_ASK_KINDS = ['date', 'propose', 'marry', 'baby', 'adopt', 'rename'] as const;
export type FamilyAskKind = (typeof FAMILY_ASK_KINDS)[number];

export const FamilyAskView = z.object({
  id: z.string().uuid(),
  kind: z.enum(FAMILY_ASK_KINDS),
  // the other person (the one asked, or the one asking)
  lifeId: z.string().uuid(),
  name: z.string(),
  mine: z.boolean(),
  spot: z.string().nullable(),
  tier: z.string().nullable(),
  venue: z.string().nullable(),
  // a baby or adoption: the name; a new name: the child and the name
  childId: z.string().uuid().nullable(),
  childName: z.string().nullable(),
  babyName: z.string().nullable(),
  pricePence: z.number().int(),
  expiresAt: z.string(),
});
export type FamilyAskView = z.infer<typeof FamilyAskView>;
export const PartnerView = z.object({
  lifeId: z.string().uuid(),
  firstName: z.string(),
  username: z.string().nullable(),
  stage: z.enum(COUPLE_STAGES),
  since: z.string(),
  stageAt: z.string().nullable(),
  closeness: z.number().int(),
  counts: z.partialRecord(z.enum(COUPLE_GOAL_KEYS), z.number().int()),
  level: z.object({
    level: z.number().int(),
    title: z.string(),
    emoji: z.string(),
    points: z.number().int(),
    next: z.number().int().nullable(),
  }),
  // when the next date can be, and the wedding (null: now)
  nextDateAt: z.string().nullable(),
  marryFrom: z.string().nullable(),
  // what stops the next step (null when it can be taken)
  blocked: z.object({
    date: z.string().nullable(),
    propose: z.string().nullable(),
    marry: z.string().nullable(),
    baby: z.string().nullable(),
    adopt: z.string().nullable(),
  }),
});
export type PartnerView = z.infer<typeof PartnerView>;
export const FamilyReply = z.object({
  partners: z.array(PartnerView),
  asks: z.array(FamilyAskView),
  maxPartners: z.number().int(),
  married: z.boolean(),
  children: z.array(ChildView),
  relatives: z.array(RelativeView),
  babies: z.object({
    visible: z.boolean(),
    maxChildren: z.number().int(),
    feedPence: z.number().int(),
    // a nanny on the payroll (Staff) looks after the babies whenever Family is opened
    nanny: z.object({ name: z.string(), emoji: z.string() }).nullable(),
  }),
  walletPence: z.number().int(),
  message: z.string().optional(),
});
export type FamilyReply = z.infer<typeof FamilyReply>;
export const FamilyAskRequest = z
  .object({
    kind: z.enum(FAMILY_ASK_KINDS),
    lifeId: z.string().uuid(),
    spot: id.optional(),
    tier: id.optional(),
    venue: placeId.optional(),
    // a baby or adoption: its name; a new name: the child (the ask goes to its other parent) and the name
    name: BabyName.optional(),
    childId: z.string().uuid().optional(),
  })
  .strict();
export type FamilyAskRequest = z.infer<typeof FamilyAskRequest>;
export const FamilyAnswerRequest = z.object({ yes: z.boolean() }).strict();
export type FamilyAnswerRequest = z.infer<typeof FamilyAnswerRequest>;
